<template>
  <div>
    <popup-hint hint="salesorders">
      <template v-slot:breadcrumbs>
        <breadcrumbs home="Sales orders">
          <template v-slot:left v-if="salesStatus.toInvoice+salesStatus.toPick+salesStatus.toShip">
            <list-notification v-model="showStatus">
              <v-list-item v-if="salesStatus.toPick">
                <v-icon>mdi-arrow-right-thin</v-icon>
                {{salesStatus.toPick}} sales order{{salesStatus.toPick===1?'':'s'}} to pick
                <v-spacer/>
                <v-btn small color="warning" @click="showStatus=false;$nextTick(() => {filters.from=null;filters.to=null;filters.customerId=null;customerName=null;filters.search=null;filters.status = 'topick'})">View</v-btn>
              </v-list-item>
              <v-list-item v-if="salesStatus.toShip">
                <v-icon>mdi-arrow-right-thin</v-icon>
                {{salesStatus.toShip}} sales order{{salesStatus.toShip===1?'':'s'}} to ship
                <v-spacer/>
                <v-btn small color="warning" @click="showStatus=false;$nextTick(() => {filters.from=null;filters.to=null;filters.customerId=null;customerName=null;filters.search=null;filters.status='toship'})">View</v-btn>
              </v-list-item>
              <v-list-item v-if="salesStatus.toInvoice">
                <v-icon>mdi-arrow-right-thin</v-icon>
                {{salesStatus.toInvoice}} sales order{{salesStatus.toInvoice===1?'':'s'}} to invoice
                <v-spacer/>
                <v-btn small color="warning" @click="showStatus=false;$nextTick(() => {filters.from=null;filters.to=null;filters.customerId=null;customerName=null;filters.search=null;filters.status='toinvoice'})">View</v-btn>
              </v-list-item>
            </list-notification>
          </template>
          <template v-slot:right>
            <v-menu offset-y v-if="company.permissions.includes('config.write')" :close-on-content-click="false" nudge-bottom="5" left>
              <template v-slot:activator="{ on }">
                <v-icon small v-on="on">mdi-tune-variant</v-icon>
              </template>
              <v-card width="400">
                <sales-preferences @update="usePicking=$event.usePicking;pickBeforeShip=$event.pickBeforeShip;lineItemPicking=$event.lineItemPicking"/>
              </v-card>
            </v-menu>
          </template>
        </breadcrumbs>
      </template>
      <template v-slot:top>
        Sales orders can be used to manage the sales process.
        From a sales order, shipments and invoices can be created.
      </template>
    </popup-hint>

    <purtrak-table name="sales orders" objectType="sales orders" :defaultColumns="defaultColumns" :headers="headers" ref="table" show-select show-expand
                   listEndPoint="salesOrders2" v-model="selected"
                   fields="id,date,due,number,customerId,customerName,privateNote,open,picked,shipped,invoiced,currency,exchangeRate,salesRepId,salesRepName,transactionId,total,discount{percent,amount,value}"
                   :totals.sync="totals" totalFields="totalValue"
                   :listFilters="listFilters"
                   @dblclick="so => $router.push({name:'SalesSalesOrdersEdit',params:{salesOrderId:so.id}})"
                   @itemExpanded="({item, value}) => expandSO(item, value)"
                   title="Sales orders"
                   :print-info="{pageSize:'landscape',
                                 subTitle:[filters.from?dateDisplay:'',
                                           filters.status!=='all'?statusText:'',
                                           filters.due=='1'?'Due':'',
                                           filters.search?searchDisplay:'',
                                           filters.salesRepId!=null?'Rep: ' + salesRepName:'']}"
                   download-endpoint="salesOrderExportCSV">
      <template v-slot:filter>
        <filter-menu include-reset @reset="filters.from=null;filters.to=null;filters.due='0';salesRepId=null;filters.status='all';filters.customerId=null;filters.search=''">
          <v-list-item>
            <filter-search :label="[{text:'Name',value:'customerName'},{text:'Number',value:'number'}]" v-model="filters.search" :search-field.sync="filters.searchField" :search-display.sync="searchDisplay"/>
          </v-list-item>
          <v-list-item>
            <date-range :from.sync="filters.from" :to.sync="filters.to" v-model="dateDisplay" option="All" allow-all/>
          </v-list-item>
          <v-list-item>
            <v-autocomplete label="Status" dense outlined :items="status" v-model="filters.status"/>
          </v-list-item>
          <v-list-item>
            <v-checkbox label="Due only" dense outlined v-model="filters.due" true-value="1" false-value="0" class="mt-n2"/>
          </v-list-item>
          <v-list-item v-if="salesReps && salesReps.length > 2 && !restrictedToSalesRep">
            <v-autocomplete label="Sales representative" dense outlined :items="salesReps" item-value="id" item-text="name" v-model="filters.salesRepId"/>
          </v-list-item>
        </filter-menu>
        <template v-if="!$vuetify.breakpoint.mobile">
          <v-chip v-show="filters.search" close small class="ml-1" @click:close="filters.search='';filters.customerId=null">{{searchDisplay}}</v-chip>
          <v-chip v-show="filters.from!=null" close small class="ml-1" @click:close="filters.from=null;filters.to=null">{{dateDisplay}}</v-chip>
          <v-chip v-show="filters.status!='all'" close small class="ml-1" @click:close="filters.status='all'">{{statusText}}</v-chip>
          <v-chip v-show="filters.due=='1'" close small class="ml-1" @click:close="filters.due='0'">Due</v-chip>
          <v-chip v-show="filters.itemId" close small class="ml-1" @click:close="filters.itemId=null">{{filters.itemName}}</v-chip>
          <v-chip v-show="filters.salesRepId!=null" close small class="ml-1" @click:close="filters.salesRepId=null">{{salesRepName}}</v-chip>
          <v-chip v-show="filters.customerName" close small class="ml-1" @click:close="filters.customerId=null;filters.customerName=''">{{filters.customerName}}</v-chip>
        </template>
      </template>
      <template v-slot:menu>
        <template v-if="!readOnly">
          <v-list-item :to="{name:'SalesSalesOrdersEditNew'}"><v-list-item-title>Create sales order</v-list-item-title></v-list-item>
          <v-divider/>
        </template>
        <template v-if="company.allowRecurringTransactions">
          <v-list-item :to="{name:'RecurringTransactions',query:{targetType:'SalesOrder'}}"><v-list-item-title>Recurring sales orders</v-list-item-title></v-list-item>
          <v-divider/>
        </template>
        <v-menu :nudge-left="220" v-if="!$vuetify.breakpoint.mobile">
          <template v-slot:activator="{on:reports}">
            <v-list-item v-on="reports">
              <v-list-item-title>Reports</v-list-item-title>
              <v-list-item-action><v-icon>mdi-chevron-right</v-icon></v-list-item-action>
            </v-list-item>
          </template>
          <v-list dense>
            <v-list-item :to="{name:'ReportsView',params:{back:[{text:'Sales orders',to:{name:'SalesSalesOrders'}}]},query:{to:'reports/sales/ReportsSalesByItem'}}"><v-list-item-title>Sales summary by item</v-list-item-title></v-list-item>
            <v-list-item :to="{name:'ReportsView',params:{back:[{text:'Sales orders',to:{name:'SalesSalesOrders'}}]},query:{to:'reports/sales/ReportsSalesByCustomer'}}"><v-list-item-title>Sales summary by customer</v-list-item-title></v-list-item>
            <v-divider/>
            <v-list-item :to="{name:'ReportsView',params:{back:[{text:'Sales orders',to:{name:'SalesSalesOrders'}}]},query:{to:'reports/sales/ReportsSalesDetailsByItem'}}"><v-list-item-title>Sales detail by item</v-list-item-title></v-list-item>
            <v-list-item :to="{name:'ReportsView',params:{back:[{text:'Sales orders',to:{name:'SalesSalesOrders'}}]},query:{to:'reports/sales/ReportsSalesDetailsByCustomer'}}"><v-list-item-title>Sales detail by customer</v-list-item-title></v-list-item>
          </v-list>
        </v-menu>
      </template>

      <template v-slot:footer v-if="selected.length">
        <v-icon style="position: absolute" class="mt-3 ml-3">mdi-arrow-up-left</v-icon>
        <v-menu offset-y top>
          <template v-slot:activator="{ on }">
            <v-btn color="primary" outlined x-small style="position: absolute" class="mt-5 ml-10" v-on="on">Batch commands</v-btn>
          </template>
          <v-list dense>
            <v-list-item @click="close()"><v-list-item-title>Close</v-list-item-title></v-list-item>
            <v-list-item @click="open()"><v-list-item-title>Open</v-list-item-title></v-list-item>
            <v-divider/>
            <v-list-item @click="print()"><v-list-item-title>Print</v-list-item-title></v-list-item>
            <v-list-item @click="deleteMultiple=true;showDelete=true"><v-list-item-title>Delete</v-list-item-title></v-list-item>
          </v-list>
        </v-menu>
      </template>
      <template v-slot:item.date="{value}">
        {{value | formatDateTime}}
      </template>
      <template v-slot:item.due="{value}">
        <template v-if="!value">TBD</template>
        <template v-else>{{value | formatDate}}</template>
      </template>
      <template v-slot:item.picked="{value,printing}">
        <template v-if="printing">{{value?'Y':''}}</template>
        <v-icon color="green" v-else-if="value">mdi-check</v-icon>
      </template>
      <template v-slot:item.shipped="{value,printing}">
        <template v-if="printing">{{value?'Y':''}}</template>
        <v-icon color="green" v-else-if="value">mdi-check</v-icon>
      </template>
      <template v-slot:item.invoiced="{value,printing}">
        <template v-if="printing">{{value?'Y':''}}</template>
        <v-icon color="green" v-else-if="value">mdi-check</v-icon>
      </template>
      <template v-slot:item.status="{item,printing}">
        <template v-if="printing || !item.open">
          {{item.open?'Open':'Closed'}}
        </template>
        <template v-else-if="item.open">
          <span class="warning--text">Open</span>
          <div v-if="!item.transactionId">
            <v-btn v-if="usePicking && !item.picked" rounded color="warning darken-2" x-small @click="pick(item)">Pick</v-btn>
            <v-btn v-else-if="!item.shipped" rounded color="warning darken-1" x-small @click="ship(item)">Ship</v-btn>
            <v-btn v-else-if="!item.invoiced" rounded color="warning" x-small @click="invoice(item)">Invoice</v-btn>
          </div>
        </template>
      </template>
      <template v-slot:item.customerName="{item,printing}">
        {{item.customerName}}
        <div v-if="!printing" class="text-caption">{{item.privateNote}}</div>
      </template>
      <template v-slot:item.total="{item,value,printing}">
        {{item.currency===homeCurrency?'':item.currency}} {{value | currency}}
        <div v-if="!printing && item.currency!==homeCurrency" class="text-caption">{{value*item.exchangeRate | currency}}</div>
      </template>
      <template v-slot:header.action v-if="!readOnly">
        <v-btn fab color="primary" small :to="{name:'SalesSalesOrdersEditNew'}"><v-icon>mdi-plus</v-icon></v-btn>
      </template>
      <template v-slot:item.action="{item}">
        <v-menu :nudge-width="150" offset-y>
          <template v-slot:activator="{ on }">
            <v-btn small v-on="on" v-if="$vuetify.breakpoint.mobile" outlined>Sales order operations</v-btn>
            <v-btn small icon v-on="on" v-else><v-icon small>mdi-dots-vertical</v-icon></v-btn>
          </template>
          <v-list dense>
            <template v-if="item.open">
              <template v-if="usePicking && (!lineItemPicking || !item.picked)">
                <v-list-item v-if="!lineItemPicking || !item.picked" @click="pick(item)"><v-list-item-title>{{item.picked?'Unpick':'Pick'}}</v-list-item-title></v-list-item>
                <v-list-item v-if="!item.picked" @click="printPickList(item)"><v-list-item-title>Print pick list</v-list-item-title></v-list-item>
                <v-divider/>
              </template>
              <v-list-item v-if="!item.shipped && (!usePicking || !pickBeforeShip || item.picked)" @click="ship(item)"><v-list-item-title>Ship</v-list-item-title></v-list-item>
              <v-list-item v-if="!item.invoiced"  @click="invoice(item)"><v-list-item-title>Invoice</v-list-item-title></v-list-item>
              <v-list-item @click="close(item)"><v-list-item-title>Close</v-list-item-title></v-list-item>
            </template>
            <template v-else>
              <v-list-item @click="open(item)"><v-list-item-title>Open</v-list-item-title></v-list-item>
            </template>
            <v-divider/>
            <v-list-item @click="print(item)"><v-list-item-title>Print</v-list-item-title></v-list-item>
            <v-divider/>
            <v-list-item :to="{name:'SalesShipments',query:{salesOrderId:item.id,salesOrderNumber:item.number}}"><v-list-item-title>View shipments</v-list-item-title></v-list-item>
            <v-list-item :to="{name:'SalesInvoices',query:{salesOrderId:item.id,salesOrderNumber:item.number}}"><v-list-item-title>View invoices</v-list-item-title></v-list-item>
            <v-list-item v-if="company.allowAudit && company.permissions.includes('audit')" :to="{name:'Audit',query:{entityId:item.id}}"><v-list-item-title>Audit history</v-list-item-title></v-list-item>
            <template v-if="!readOnly">
              <v-divider/>
              <v-list-item :to="{name:'SalesSalesOrdersEdit',params:{salesOrderId:item.id}}"><v-list-item-title>Edit sales order</v-list-item-title></v-list-item>
              <v-list-item :to="{name:'SalesSalesOrdersEditNew',params:{copySalesOrderId:item.id}}"><v-list-item-title>Copy sales order</v-list-item-title></v-list-item>
              <v-list-item @click="updatePrivateNote(item)"><v-list-item-title>Update private note</v-list-item-title></v-list-item>
              <v-list-item @click="so=item;deleteMultiple=false;showDelete=true"><v-list-item-title>Delete sales order</v-list-item-title></v-list-item>
            </template>
          </v-list>
        </v-menu>
      </template>
      <template v-slot:body.append="{headers,printing}" v-if="!$vuetify.breakpoint.mobile">
        <tr :class="darkMode ? 'total-row-dark' : 'total-row'">
          <template v-for="header of headers">
            <td class="text-right" v-if="header.value==='total' && values">
              {{totals.totalValue | currency}}
            </td>
            <td v-else-if="!printing || header.print === undefined || header.print"/>
          </template>
        </tr>
      </template>
      <template v-slot:expanded-item="{ headers,item }">
        <td :colspan="headers.length">
          <v-card class="my-4" :class="expansionPanelClass">
          <v-simple-table dense class="pl-4 text-caption" :class="expansionPanelClass" style="width:80%">
            <thead>
              <tr>
                <th :class="expansionPanelClass" width="36%">Description</th>
                <th class="text-right" :class="expansionPanelClass" width="16%">Quantity</th>
                <th v-if="lineItemPicking" class="text-right" :class="expansionPanelClass" width="10%">Picked</th>
                <th class="text-right" :class="expansionPanelClass" width="10%">Shipped</th>
                <th class="text-right" :class="expansionPanelClass" width="10%">Invoiced</th>
                <th class="text-right" :class="expansionPanelClass" width="10%">Unit price</th>
                <th class="text-right" :class="expansionPanelClass" width="10%">Amount</th>
              </tr>
            </thead>
            <tbody>
              <template v-for="line of item.lines">
                <tr :key="line.key">
                  <td class="text-caption">{{line.description}}</td>
                  <td class="text-caption text-right">
                    {{line.quantity| number(line.precision)}} {{line.units}}
                  </td>
                  <td v-if="lineItemPicking" class="text-caption text-right">
                    {{line.picked | number(line.precision)}}
                  </td>
                  <td class="text-caption text-right">
                    {{line.shippedQuantity | number(line.precision)}}
                  </td>
                  <td class="text-caption text-right">
                    {{line.invoicedQuantity | number(line.precision)}}
                  </td>
                  <template v-if="!line.items">
                    <td class="text-caption text-right">${{line.unitPrice | number(4)}}</td>
                    <td class="text-caption text-right">{{line.amount | currency}}</td>
                  </template>
                  <td v-else colspan="2"/>
                </tr>
                <tr v-for="bundlineLine of line.items" :key="bundlineLine.key">
                  <td class="text-caption"><span class="ml-4">{{bundlineLine.description}}</span></td>
                  <td class="text-caption text-right">{{line.quantity}}x{{bundlineLine.quantity | number(bundlineLine.precision)}} {{bundlineLine.units}}</td>
                  <td/>
                  <td/>
                  <td class="text-caption text-right">${{bundlineLine.unitPrice | number(4)}}</td>
                  <td class="text-caption text-right">{{bundlineLine.amount | currency}}</td>
                </tr>
              </template>
              <tr v-if="item.discount.amount || (item.taxLines && item.taxLines.length)">
                <td class="text-caption text-right" :colspan="5+(lineItemPicking?1:0)">Subtotal</td>
                <td class="text-caption text-right">{{item.subTotal | currency}}</td>
              </tr>
              <tr v-if="item.discount.amount">
                <td class="text-caption text-right" :colspan="5+(lineItemPicking?1:0)">Discount
                  <template v-if="item.discount.percent">{{item.discount.amount | number(2)}}%</template>
                  <template v-else>{{item.discount.amount | currency}}</template>
                </td>
                <td class="text-caption text-right">- {{item.discount.value | currency}}</td>
              </tr>
              <template v-for="tax of item.taxLines">
                <tr :key="tax.id">
                  <td class="text-caption text-right" :colspan="5+(lineItemPicking?1:0)">
                    {{tax.name}} @ {{tax.percent}}% on {{tax.onAmount | currency}}
                  </td>
                  <td class="text-caption text-right">{{tax.amount | currency}}</td>
                </tr>
              </template>
              <tr>
                <td class="text-caption text-right" :colspan="5+(lineItemPicking?1:0)">Total</td>
                <td class="text-caption text-right">{{item.total | currency}}</td>
              </tr>
            </tbody>
          </v-simple-table>
          </v-card>
        </td>
      </template>
    </purtrak-table>

    <component :is="dialog" v-bind="dialogProperties" @done="dialog = null"/>
    <delete-object v-model="showDelete" :name="deleteMultiple ? 'ALL selected sales orders' : 'this sales order'" @delete="performDelete(so.id)"/>
  </div>
</template>

<script>
import performDelete from '@/mixins/performDelete'
import SalesPreferences from '@/views/sales/SalesPreferences'
import {format} from 'date-fns'

export default {
  name: 'SalesSalesOrders',
  mixins: [performDelete],
  components: {SalesPreferences},
  data() {
    return {
      filters: {
        search: '',
        searchField: 'customerName',
        from: null,
        to: null,
        future: '1',
        customerId: null,
        itemId: null,
        itemName: null,
        open: '2',
        due: '0',
        salesRepId: null,
        status: 'all'
      },
      totals: {totalValue:0},
      salesStatus: {},
      showStatus: false,
      usePicking: false,
      lineItemPicking: false,
      pickBeforeShip: false,
      dateDisplay: '',
      searchDisplay: '',
      salesReps: [{id:null,name:'All'},{divider:true}],
      so: {},
			dialog: null,
      dialogProperties: {},
      showDelete: false,
      deleteMultiple: false,
      defaultColumns: [{ value: 'date', visible:true},
                       { value: 'number', visible:true},
                       { value: 'due', visible:false},
                       { value: 'customerName', visible:true},
                       { value: 'total', visible:true},
                       { value: 'privateNote', visible:false},
                       { value: 'salesRepName', visible:false},
                       { value: 'picked', visible:true},
                       { value: 'shipped', visible:true},
                       { value: 'invoiced', visible:true},
                       { value: 'status', visible:true}],
      headers: [{ text: 'DATE', value: 'date', width:'15%',sortable:true},
                { text: 'NUMBER', value: 'number' ,width:'10%',sortable:true},
                { text: 'EXPECTED', value: 'due' ,width:'15%',sortable:true},
                { text: 'CUSTOMER', value: 'customerName' ,width:'30%',sortable:true},
                { text: 'NOTE', value: 'privateNote' ,width:'25%',sortable:false},
                { text: 'TOTAL', value: 'total' ,width:'15%',sortable:true,align:'right'},
                { text: 'SALES REP', value: 'salesRepName' ,width:'20%',sortable:true},
                { text: 'PICKED', value: 'picked' ,width:'10%',sortable:true,align:'center',omit:() => !this.usePicking},
                { text: 'SHIPPED', value: 'shipped' ,width:'10%',sortable:true,align:'center'},
                { text: 'INVOICED', value: 'invoiced' ,width:'10%',sortable:true,align:'center'},
                { text: 'STATUS', value: 'status' ,width:'20%',sortable:false,align:'center'}]
    }
  },
  computed: {
    company() {
      return this.$store.getters.company;
    },
    readOnly() {
      return !this.company.permissions.includes('selling.write');
    },
    homeCurrency() {
      return this.company.settings.homeCurrency;
    },
    status() {
      const status = [{value:'all',text:'All'},{divider:true},{value:'open',text:'Open'},{value:'closed',text:'Closed'},{divider:true}];
      if (this.usePicking)
        status.push({value:'topick',text:'To pick'});
      status.push(...[{value:'toship',text:'To ship'},{value:'toinvoice',text:'To invoice'}]);
      return status;
    },
    statusText() {
      return this.status.find(s => s.value === this.filters.status)?.text;
    },
    listFilters() {
      const filters = [];
      const today = format(new Date(), 'yyyy-MM-dd');
      if (this.filters.from)
        filters.push({field:'date', comparator:'ge', value:`${this.filters.from} 00:00:00`});
      if (this.filters.to)
        filters.push({field:'date', comparator:'le', value:`${this.filters.to} 23:59:59`});
      if (this.filters.salesRepId)
        filters.push({field:'salesRepId', comparator:'eq', value:this.filters.salesRepId});
      if (this.filters.customerId !== null)
        filters.push({field:'customerId', comparator:'eq', value:this.filters.customerId});
      if (this.filters.search)
        filters.push({field:this.filters.searchField, comparator:'contains', value:this.filters.search});
      if (this.filters.due == '1')
        filters.push({field:'open', comparator:'eq', value:'true'},
                     {field:'due', comparator:'le', value:today});
      if (this.filters.status !== 'all') {
        if (this.filters.status === 'open')
          filters.push({field:'open', comparator:'eq', value:'true'});
        if (this.filters.status === 'closed')
          filters.push({field:'open', comparator:'eq', value:'false'});
        if (this.filters.status === 'topick')
          filters.push({field:'open', comparator:'eq', value:'true'},
                      {field:'picked', comparator:'eq', value:'false'});
        if (this.filters.status === 'toship')
          filters.push({field:'open', comparator:'eq', value:'true'},
                       {field:'picked', comparator:'eq', value:'true'},
                       {field:'shipped', comparator:'eq', value:'false'});
        if (this.filters.status === 'toinvoice')
          filters.push({field:'open', comparator:'eq', value:'true'},
//                       {field:'shipped', comparator:'eq', value:'true'},
                       {field:'invoiced', comparator:'eq', value:'false'});
      }
      return filters;
    },
    salesRepName() {
      return this.salesReps?.find(salesRep => salesRep.id === this.filters.salesRepId)?.name ?? '';
    },
    restrictedToSalesRep() {
      return this.salesReps?.find(rep => rep.userId === this.$store.state.user.sub && rep.restricted);
    },
    values() {
      return this.$store.getters.company.permissions.includes('values');
    }
  },
  async created() {
    this.$store.dispatch('checkTasks');
    this.$lazyListLoad(this.salesReps, 'salesReps2', 'id,name,restricted,userId', 100, [{field:'active', comparator:'eq', value:'true'}]);

    const results = await this.$apiCall(`query SalesSalesOrders {
                                            salesPreferences {
                                              usePicking,pickBeforeShip,lineItemPicking
                                            }
                                          }`);
    if (!results.error) {
      this.usePicking = results.data.salesPreferences.usePicking;
      this.lineItemPicking = results.data.salesPreferences.lineItemPicking;
      this.pickBeforeShip = results.data.salesPreferences.pickBeforeShip;
    }
    await this.getStatus();
    if (!['SalesSalesOrdersEdit', 'SalesSalesOrdersEditNew'].includes(this._previousRoute) &&
        Object.keys(this.$route.query).length === 0 &&
        (this.salesStatus.toInvoice + this.salesStatus.toPick + this.salesStatus.toShip) && !localStorage.getItem('hideNotification')) {
      this.$nextTick(() => this.showStatus = true);
      setTimeout(() => this.showStatus = false, 10_000);  // Close in 10 seconds
    }
  },
  methods: {
    print(so) {
      this.dialogProperties = {salesOrder:so, salesOrders:this.selected};
      this.dialog = () => import(`@/views/sales/salesOrders/SalesSalesOrdersPrint`);
    },
    printPickList(so) {
      this.dialogProperties = {salesOrder:so, salesOrders:this.selected};
      this.dialog = () => import(`@/views/sales/salesOrders/SalesSalesOrdersPickListPrint`);
    },
    async expandSO(item, value) {
      if (value && !item.lines) {
        this.loading = true;
        const results = await this.$apiCall(`query SalesSalesOrders($id:ID!) {
                                                salesOrder(id:$id) {
                                                  subTotal
                                                  lines {
                                                    description,quantity,picked,shippedQuantity,invoicedQuantity,unitPrice,amount,precision,units
                                                    items {description,quantity,units,precision,unitPrice,amount,id}
                                                  }
                                                  taxLines {id,name,amount,onAmount,percent}
                                                }
                                              }`,
                                            {id:item.id});
        if (!results.error) {
          item.subTotal = results.data.salesOrder.subTotal;
          this.$set(item, 'lines', results.data.salesOrder.lines);
          this.$set(item, 'taxLines', results.data.salesOrder.taxLines);
          let key = 1;
          item.lines.forEach(line => line.key = key++);
        }
        this.loading = false;
      }
    },
    async getStatus() {
      this.loading = true;
      const results = await this.$apiCall(`query SalesSalesOrders {
                                                salesStatus {
                                                  toPick,toShip,toInvoice
                                                }
                                              }`);
      if (!results.error) {
        this.salesStatus = results.data.salesStatus;
        if (!this.usePicking)
          this.salesStatus.toPick = 0;
      }
      this.loading = false;
    },
    async pick(so) {
      if (this.lineItemPicking) {
        this.$router.push({name:'SalesSalesOrdersEdit',params:{salesOrderId:so.id,pick:true}});
        return;
      }
      this.loading = true;
      const results = await this.$apiCall(`mutation SalesSalesOrders($id: ID!, $picked:Boolean!) {
                                              salesOrderPick(id:$id, picked:$picked) {
                                                picked
                                              }
                                            }`,
                                          {id:so.id, picked:!so.picked});
      this.loading = false;
      if (!results.error) {
        so.picked = results.data.salesOrderPick.picked;
        this.$store.commit('setError', {type:'success',message:`Sales order for ${so.customerName} ${so.picked?'picked':'unpicked'}`});
      }
      this.getStatus();
    },
    async ship(so) {
      // First look to see if there are draft shipments for this order
      const results = await this.$apiCall(`query SalesSalesOrders($filters:[FilterInput]!) {
                                              shipments2(first:100, filters:$filters) {
                                                edges {
                                                  node {date, id}
                                                }
                                              }
                                            }`,
                                          {filters:[{field:'salesOrderId', comparator:'eq', value:so.id},{field:'draft', comparator:'eq', value:'true'}]});
      if (!results.error && results.data.shipments2.edges.length) {
        this.dialogProperties = {salesOrder:so, draftShipments:results.data.shipments2.edges.map(edge => edge.node)};
        this.dialog = () => import(`@/views/sales/salesOrders/SalesSalesOrdersPickShipment`);
      }
      else
        this.$router.push({name:'SalesShipmentsEditNew',params:{salesOrderId:so.id}});
    },
    async invoice(so) {
      // First look to see if there are draft invoices for this order
      const results = await this.$apiCall(`query SalesSalesOrders($filters:[FilterInput]!) {
                                              invoices2(first:100, filters:$filters) {
                                                edges {
                                                  node {date, id}
                                                }
                                              }
                                            }`,
                                          {filters:[{field:'salesOrderId', comparator:'eq', value:so.id},{field:'draft', comparator:'eq', value:'true'}]});
      if (!results.error && results.data.invoices2.edges.length) {
        this.dialogProperties = {salesOrder:so, draftInvoices:results.data.invoices2.edges.map(edge => edge.node)};
        this.dialog = () => import(`@/views/sales/salesOrders/SalesSalesOrdersPickInvoice`);
      }
      else
        this.$router.push({name:'SalesInvoicesEditNew',params:{salesOrderId:so.id}});
    },
    async close(so) {
      this.loading = true;
      if (so) {
        const results = await this.$apiCall(`mutation SalesSalesOrders($id: ID!) {
                                                salesOrderClose(id:$id) {
                                                  open
                                                }
                                              }`,
                                            {id:so.id});
        if (!results.error) {
          so.open = results.data.salesOrderClose.open;
          this.$store.commit('setError', {type:'success',message:`Sales order for ${so.customerName} is ${so.open?'open':'closed'}`});
        }
      }
      else {
        let success = 0;
        for (const so of this.selected) {
          const results = await this.$apiCall(`mutation SalesSalesOrders($id: ID!) {
                                                  salesOrderClose(id:$id) {
                                                    open
                                                  }
                                                }`,
                                              {id:so.id});
          if (!results.error) {
            so.open = results.data.salesOrderClose.open;
            success += so.open ? 0 : 1;
          }
        }
        this.$store.commit('setError', {type:'success',message:`Closed ${success} of ${this.selected.length} sales orders`});
      }
      this.getStatus();
      this.loading = false;
    },
    async open(so) {
      this.loading = true;
      if (so) {
        const results = await this.$apiCall(`mutation SalesSalesOrders($id: ID!) {
                                                salesOrderOpen(id:$id) {
                                                  open
                                                }
                                              }`,
                                            {id:so.id});
        if (!results.error) {
          so.open = results.data.salesOrderOpen.open;
          this.$store.commit('setError', {type:'success',message:`Sales order for ${so.customerName} ${so.open?'is now open':'cannot be changed to open as it has been completely shipped and invoiced against.'}`});
        }
      }
      else {
        let success = 0;
        for (const so of this.selected) {
          const results = await this.$apiCall(`mutation SalesSalesOrders($id: ID!) {
                                                  salesOrderOpen(id:$id) {
                                                    open
                                                  }
                                                }`,
                                              {id:so.id});
          if (!results.error) {
            so.open = results.data.salesOrderOpen.open;
            success += so.open ? 1 : 0;
          }
        }
        this.$store.commit('setError', {type:'success',message:`Opened ${success} of ${this.selected.length} sales orders`});
      }
      this.getStatus();
      this.loading = false;
    },
    updatePrivateNote(salesOrder) {
      this.dialogProperties = {item:salesOrder, operation:'salesOrderUpdatePrivateNote'};
      this.dialog = () => import(`@/components/UpdatePrivateNote`);
    }
  }
}
</script>